import { makeAuthenticatedRequest, getCMSJWT } from "./auth.service";
import { storeService } from './store.service';
import {
  CMS_BASE_URL,
  CMS_ENVIRONMENT_QUERY,
} from "../config/api.config";
import { storageService } from "./storage.service";
import {
  PerkDetail,
  PerkEntitlement,
  PerkSummary,
  PerkWallet,
} from "../types/perks.types";

/**
 * MPG Perks for the signed-in customer, through Strapi's /api/perks proxy.
 *
 * The wallet is cached briefly in local storage the way appointments are —
 * a versioned key with a short TTL — and busted on claim, because the very
 * next thing the customer sees after claiming is the code that claiming
 * produced.
 */
const WALLET_CACHE_KEY = "perks_wallet_cache_v1";
const WALLET_CACHE_TTL_MS = 2 * 60 * 1000;

interface WalletCache {
  wallet: PerkWallet;
  timestamp: number;
}

const EMPTY_WALLET: PerkWallet = {
  enabled: true,
  completedGroomCount: 0,
  membership: { isActive: false },
  members: [],
  available: [],
  claimed: [],
  enjoyed: [],
  upcoming: [],
};

/** A wallet cached before member perks existed has no shelf for them; give it an empty one. */
const withMembers = (w: PerkWallet): PerkWallet => ({
  ...w,
  membership: w.membership ?? { isActive: false },
  members: w.members ?? [],
});

const readCache = (): PerkWallet | null => {
  const raw = storageService.getItem(WALLET_CACHE_KEY);
  if (!raw) return null;
  try {
    const cached: WalletCache = JSON.parse(raw);
    if (Date.now() - cached.timestamp > WALLET_CACHE_TTL_MS) return null;
    return cached.wallet;
  } catch {
    return null;
  }
};

const writeCache = (wallet: PerkWallet) => {
  storageService.setItem(
    WALLET_CACHE_KEY,
    JSON.stringify({ wallet, timestamp: Date.now() } as WalletCache)
  );
};

const parseError = async (response: Response): Promise<string> => {
  try {
    const text = await response.text();
    try {
      const body = JSON.parse(text);
      // The API's refusal is { error, details }: `error` is only the category
      // ("Business Rule Violated"), which tells a customer nothing; `details`
      // is the sentence ("This perk isn't connected to the partner's store
      // yet…"). Found on a perk claim, 2 Nov 2026 (as waitlist.service.ts).
      const pick = [body?.details, body?.message, body?.detail, body?.error];
      return pick.find((v): v is string => typeof v === 'string' && v.trim().length > 0) ?? text;
    } catch {
      return text;
    }
  } catch {
    return `Request failed: ${response.status}`;
  }
};

export const perksService = {
  clearCache(): void {
    storageService.removeItem(WALLET_CACHE_KEY);
  },

  async fetchWallet(options: { force?: boolean } = {}): Promise<PerkWallet> {
    if (!options.force) {
      const cached = readCache();
      if (cached) return withMembers(cached);
    }
    const response = await makeAuthenticatedRequest(
      `${CMS_BASE_URL}/api/perks/wallet`,
      { method: "GET" }
    );
    if (!response.ok) {
      throw new Error(`Failed to fetch perks: ${response.status}`);
    }
    const data = await response.json();
    const wallet: PerkWallet = { ...EMPTY_WALLET, ...data };
    writeCache(wallet);
    return withMembers(wallet);
  },

  /**
   * The tenant's public catalogue — the offers, nobody's wallet. No customer,
   * no tokens, no login: this is what a logged-out visitor sees, and the same
   * route the public site's /perks page uses.
   */
  async fetchCatalog(companyId?: string | number): Promise<PerkSummary[]> {
    const id = companyId ?? (await storeService.getCompanyId());
    const response = await fetch(
      `${CMS_BASE_URL}/api/perks/catalog?companyId=${encodeURIComponent(
        String(id)
      )}${CMS_ENVIRONMENT_QUERY}`,
      { method: "GET" }
    );
    if (!response.ok) {
      throw new Error(
        `Failed to fetch the perks catalogue: ${response.status}`
      );
    }
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  },

  async fetchPerk(idOrSlug: string): Promise<PerkDetail | null> {
    const response = await makeAuthenticatedRequest(
      `${CMS_BASE_URL}/api/perks/${encodeURIComponent(idOrSlug)}`,
      { method: "GET" }
    );
    if (response.status === 404) return null;
    if (!response.ok) {
      throw new Error(`Failed to fetch perk: ${response.status}`);
    }
    return response.json();
  },

  /**
   * Available → Claimed. The server is idempotent, so re-opening the code
   * screen is safe. Busts the wallet cache: the code must show immediately.
   */
  async claim(entitlementId: number): Promise<PerkEntitlement> {
    const response = await makeAuthenticatedRequest(
      `${CMS_BASE_URL}/api/perks/entitlements/${entitlementId}/claim`,
      { method: "POST" }
    );
    if (!response.ok) {
      throw new Error(await parseError(response));
    }
    const data = await response.json();
    this.clearCache();
    return data.entitlement as PerkEntitlement;
  },

  /**
   * How many perks are ready to use or waiting to be claimed — the menu
   * badge. Cheap (served from the wallet cache when fresh) and never throws:
   * a signed-out visitor simply has none.
   */
  async countReadyPerks(): Promise<number> {
    if (!getCMSJWT()) return 0;
    try {
      const wallet = await this.fetchWallet();
      if (!wallet.enabled) return 0;
      return wallet.available.length + wallet.claimed.length;
    } catch {
      return 0;
    }
  },
};

/** "$20 off", "20% off", or the title for a freebie — the card headline lead. */
export const perkValueLabel = (perk: PerkSummary): string => {
  if (perk.valueKind === "amountOff" && perk.valueAmount != null)
    return `$${perk.valueAmount} off`;
  if (perk.valueKind === "percentOff" && perk.valueAmount != null)
    return `${perk.valueAmount}% off`;
  return "Free";
};

export const formatPerkDate = (iso?: string | null): string =>
  iso
    ? new Date(iso).toLocaleDateString("en-AU", {
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    : "";

export const formatPerkDateShort = (iso?: string | null): string =>
  iso
    ? new Date(iso).toLocaleDateString("en-AU", {
        day: "numeric",
        month: "long",
      })
    : "";

/**
 * "Complete Bella's next groom" — the pet name from the customer's cached
 * pets when they have exactly one; "your pet" otherwise, so a multi-pet
 * household is never told the wrong name.
 */
/** "Bella" → "Bella's"; "Chris" → "Chris'". Names ending in s take the bare apostrophe. */
export const possessiveOf = (name: string): string => {
  const n = name.trim();
  return n.endsWith("s") ? `${n}'` : `${n}'s`;
};

export const possessivePetName = (
  pets: Array<{ name?: string }> | null | undefined
): string => {
  const names = (pets ?? []).map((p) => (p?.name ?? "").trim()).filter(Boolean);
  if (names.length !== 1) return "your pet's";
  return possessiveOf(names[0]);
};
